import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SKILL_ACCESS_WHO_CAN_RUN, type SkillRunDTO } from "@tabbio/types";
import { useSkillAccessSettings } from "../../../hooks/useSkillAccessSettings";
import { useSettingsDetail } from "../../../hooks/useSettingsDetail";
import { useToast } from "../../../hooks/useToast";
import { confirm } from "../../../lib/confirm-action";
import type { UpdateSkillSettingsInput } from "../../../lib/skills-api";
import { SettingsDetailSurface } from "./SettingsDetailSurface";
import {
  ControlStack,
  ControlHeading,
  ControlSection,
  ControlRow,
  ControlCopy,
  ControlToggle,
  ControlAction,
} from "./DataControlParts";

type Detail = "who" | "runs" | "blockedSkills" | "blockedCreators";

export function AgentAccessPanel() {
  const { t, i18n } = useTranslation("common");
  const toast = useToast();
  const surface = useSettingsDetail<Detail>("who");
  const { settings, runs, save, undo } = useSkillAccessSettings(
    surface.visible && surface.detail === "runs",
  );
  const [busy, setBusy] = useState(false);
  const prefs = settings.data;
  const persist = async (patch: UpdateSkillSettingsInput) => {
    setBusy(true);
    try {
      return await save(patch);
    } catch {
      toast.error(t("settings.agentAccess.saveFailed"));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const handleUndo = async (run: SkillRunDTO) => {
    if (busy) return;
    const accepted = await confirm({
      title: t("settings.agentAccess.undoTitle"),
      message: t("settings.agentAccess.undoMessage", { skill: run.skillId }),
      confirmLabel: t("settings.agentAccess.undo"),
      cancelLabel: t("actions.cancel"),
      destructive: true,
    });
    if (!accepted) return;
    setBusy(true);
    try {
      if (await undo(run.id)) toast.success(t("settings.agentAccess.undone"));
    } catch {
      toast.error(t("settings.agentAccess.undoFailed"));
    } finally {
      setBusy(false);
    }
  };
  const detailTitle = t(
    `settings.agentAccess.${surface.detail === "who" ? "whoCanRun" : surface.detail === "runs" ? "runLog" : surface.detail}`,
  );
  const blockedKey =
    surface.detail === "blockedCreators"
      ? "blockedCreatorIds"
      : "blockedSkillIds";
  return (
    <ControlStack gap={24}>
      <ControlHeading
        title={t("settings.categories.agentAccess")}
        description={t("settings.agentAccess.subtitle")}
      />
      {settings.isError ? (
        <ControlStack>
          <ControlCopy>{t("settings.agentAccess.loadFailed")}</ControlCopy>
          <ControlAction
            title={t("settings.dataManagement.retry")}
            onPress={() => void settings.refetch()}
          />
        </ControlStack>
      ) : !prefs ? (
        <ControlCopy>{t("labels.loading")}</ControlCopy>
      ) : (
        <>
          <ControlSection title={t("settings.agentAccess.permissions")}>
            <ControlRow
              title={t("settings.agentAccess.whoCanRun")}
              value={t(`settings.agentAccess.who.${prefs.whoCanRunSkills}`)}
              icon="privacy"
              onPress={() => surface.open("who")}
            />
            <ControlToggle
              title={t("settings.agentAccess.communityToggle")}
              description={t("settings.agentAccess.communityToggleDescription")}
              checked={prefs.communitySkillsEnabled}
              disabled={busy}
              onChange={(value) =>
                void persist({ communitySkillsEnabled: value })
              }
            />
            <ControlToggle
              title={t("settings.agentAccess.confirmFullCv")}
              description={t("settings.agentAccess.confirmFullCvDescription")}
              checked={prefs.confirmFullCvWrite}
              disabled={busy}
              onChange={(value) => void persist({ confirmFullCvWrite: value })}
            />
          </ControlSection>
          <ControlSection title={t("settings.agentAccess.activityAndBlocks")}>
            <ControlRow
              title={t("settings.agentAccess.runLog")}
              icon="memory"
              onPress={() => surface.open("runs")}
            />
            <ControlRow
              title={t("settings.agentAccess.blockedSkills")}
              value={prefs.blockedSkillIds.length.toLocaleString(i18n.language)}
              onPress={() => surface.open("blockedSkills")}
            />
            <ControlRow
              title={t("settings.agentAccess.blockedCreators")}
              value={prefs.blockedCreatorIds.length.toLocaleString(
                i18n.language,
              )}
              onPress={() => surface.open("blockedCreators")}
            />
          </ControlSection>
        </>
      )}
      <SettingsDetailSurface
        visible={surface.visible}
        title={detailTitle}
        onClose={surface.close}
        onAfterClose={surface.afterClose}
      >
        {surface.detail === "who" ? (
          SKILL_ACCESS_WHO_CAN_RUN.map((value) => (
            <ControlRow
              key={value}
              title={t(`settings.agentAccess.who.${value}`)}
              selected={prefs?.whoCanRunSkills === value}
              disabled={busy}
              onPress={() => void persist({ whoCanRunSkills: value })}
            />
          ))
        ) : surface.detail === "runs" ? (
          runs.isError ? (
            <ControlStack>
              <ControlCopy>
                {t("settings.agentAccess.runsLoadFailed")}
              </ControlCopy>
              <ControlAction
                title={t("settings.dataManagement.retry")}
                onPress={() => void runs.refetch()}
              />
            </ControlStack>
          ) : runs.isPending ? (
            <ControlCopy>{t("labels.loading")}</ControlCopy>
          ) : !runs.data?.runs.length ? (
            <ControlCopy>{t("settings.agentAccess.runLogEmpty")}</ControlCopy>
          ) : (
            runs.data.runs.map((run) => (
              <ControlStack key={run.id} gap={8}>
                <ControlRow
                  title={run.skillId}
                  description={[
                    t(`settings.agentAccess.runStatus.${run.status}`, {
                      defaultValue: String(run.status),
                    }),
                    run.dryRun ? t("settings.agentAccess.dryRunBadge") : "",
                    Number.isNaN(new Date(run.createdAt).getTime())
                      ? ""
                      : new Date(run.createdAt).toLocaleDateString(
                          i18n.language,
                        ),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                />
                {!run.dryRun && run.resultCvId && run.status === "completed" ? (
                  <ControlAction
                    title={t("settings.agentAccess.undo")}
                    disabled={busy}
                    onPress={() => void handleUndo(run)}
                  />
                ) : null}
              </ControlStack>
            ))
          )
        ) : prefs?.[blockedKey].length ? (
          prefs[blockedKey].map((id) => (
            <ControlStack key={id} gap={8}>
              <ControlCopy title>{id}</ControlCopy>
              <ControlAction
                title={t("settings.agentAccess.unblock")}
                disabled={busy}
                onPress={() =>
                  void persist({
                    [blockedKey]: prefs[blockedKey].filter(
                      (item) => item !== id,
                    ),
                  })
                }
              />
            </ControlStack>
          ))
        ) : (
          <ControlCopy>{t("settings.agentAccess.noBlocked")}</ControlCopy>
        )}
      </SettingsDetailSurface>
    </ControlStack>
  );
}
